import { useState, useEffect } from 'react';
import { vendorScoresAPI } from '@/lib/api';

const scoreCache = {};
let pendingBatch = [];
let batchTimer = null;
let batchResolver = null;

function flushBatch(token) {
  const names = [...new Set(pendingBatch)];
  pendingBatch = [];
  const resolve = batchResolver;
  batchResolver = null;

  vendorScoresAPI.batch(names).then(res => {
    Object.assign(scoreCache, res.data || {});
    if (resolve) resolve();
  }).catch(() => {
    if (resolve) resolve();
  });
}

function requestScore(vendorName) {
  if (scoreCache[vendorName] !== undefined) return;
  pendingBatch.push(vendorName);
  if (batchTimer) clearTimeout(batchTimer);
  return new Promise(resolve => {
    batchResolver = resolve;
    batchTimer = setTimeout(() => flushBatch(), 200);
  });
}

export function VendorScoreDot({ vendorName, className = '' }) {
  const [score, setScore] = useState(scoreCache[vendorName] || null);
  const [loaded, setLoaded] = useState(!!scoreCache[vendorName]);

  useEffect(() => {
    if (!vendorName) return;
    if (scoreCache[vendorName]) {
      setScore(scoreCache[vendorName]);
      setLoaded(true);
      return;
    }
    requestScore(vendorName)?.then(() => {
      setScore(scoreCache[vendorName] || null);
      setLoaded(true);
    });
  }, [vendorName]);

  if (!vendorName || !loaded) return null;

  const s = score;
  const composite = s?.composite_score;
  const hasSufficientData = s && s.total_pos >= 2;

  let color = '#9CA3AF'; // grey - new vendor
  let label = 'New vendor — insufficient data';

  if (hasSufficientData && composite != null) {
    if (composite > 80) {
      color = '#16A34A';
      label = `Vendor Rating: ${Math.round(composite)}/100`;
    } else if (composite >= 60) {
      color = '#EAB308';
      label = `Vendor Rating: ${Math.round(composite)}/100`;
    } else {
      color = '#DC2626';
      label = `Vendor Rating: ${Math.round(composite)}/100`;
    }
    const del = s.delivery_reliability_pct != null ? `Delivery: ${Math.round(s.delivery_reliability_pct)}%` : '';
    const qual = s.quality_pass_rate_pct != null ? `Quality: ${Math.round(s.quality_pass_rate_pct)}%` : '';
    if (del || qual) label += ` (${[del, qual].filter(Boolean).join(' | ')})`;
  }

  return (
    <span
      className={`inline-flex items-center ${className}`}
      title={label}
      data-testid={`vendor-score-dot-${vendorName?.replace(/\s/g, '-')}`}
    >
      <span
        className="w-2 h-2 rounded-full inline-block"
        style={{ backgroundColor: color }}
      />
    </span>
  );
}

export default VendorScoreDot;
